import React, { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import Badge from '../../components/ui/Badge';
import { PrinterIcon } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ReportTextCell, CrewDataTableFrame, CrewReportViewAction , useCrewReportOpen } from './reportShared';
import { statsAvailablePrintPath } from './reports/statsAvailablePrintMeta';
import { statsCompliancePrintPath } from './reports/statsCompliancePrintMeta';
import { statsNotRecommendedPrintPath } from './reports/statsNotRecommendedPrintMeta';
import { statsOnboardPrintPath } from './reports/statsOnboardPrintMeta';
import {
  COMPLIANCE_META,
  isComplianceFocus,
} from './complianceFocus';
import {
  BucketBars,
  formatStatNum,
  HorizontalBars,
  KpiTile,
  kpiTone,
  StatsPageShell} from './statisticsShared';
import type {
  CrewStatMixItem,
  CrewStatsAppraisalRow,
  CrewStatsAvailableRow,
  CrewStatsCertRow,
  CrewStatsCoeRow,
  CrewStatsMedicalRow,
  CrewStatsNrRow,
  CrewStatsOnboardRow,
  CrewStatsWageRow} from './types';

const CrewNameLink: React.FC<{ crewId: number; name: string }> = ({ crewId, name }) => {
  const { colors } = useTheme();
  const { listReturnPath } = useCrewReportOpen();
  return (
    <Link
      to={`/crew/${crewId}`}
      state={{ from: listReturnPath }}
      className={cn('text-sm hover:underline', colors.primaryFg)}
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  );
};

const crewLink = (crewId: number, name: string) => <CrewNameLink crewId={crewId} name={name} />;

type OpsFocus = 'onboard' | 'available' | 'not-recommended';

const OPS_META: Record<
  OpsFocus,
  { title: string; body: string; kpiLabel: string; empty: string }
> = {
  onboard: {
    title: 'Onboard',
    body: 'Currently onboard crew with active ONB assignments.',
    kpiLabel: 'Onboard',
    empty: 'No onboard crew',
  },
  available: {
    title: 'Available',
    body: 'Crew in the relief pool with availability dates or status (excluding not-recommended).',
    kpiLabel: 'Available',
    empty: 'No available crew',
  },
  'not-recommended': {
    title: 'Not recommended',
    body: 'Crew flagged as not recommended, with last appraisal, evaluation average, and last tour context.',
    kpiLabel: 'Not recommended',
    empty: 'No not-recommended crew',
  },
};

const isOpsFocus = (v: string | undefined): v is OpsFocus =>
  v === 'onboard' || v === 'available' || v === 'not-recommended';

export const OperationsStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const focus = isOpsFocus(focusParam) ? focusParam : null;
  const meta = focus ? OPS_META[focus] : null;

  const onboardColumns: Column<CrewStatsOnboardRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'signOnDate',
        header: 'Sign on',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      { key: 'monthsOnboard', header: 'Months', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const availableColumns: Column<CrewStatsAvailableRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'availableFrom',
        header: 'Av. from',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      {
        key: 'availableTo',
        header: 'Av. to',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      {
        key: 'availabilityStatus',
        header: 'Av. status',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
      { key: 'lastVessel', header: 'Last vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'lastRank', header: 'Last rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const nrColumns: Column<CrewStatsNrRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      {
        key: 'crewRef',
        header: 'Ref',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'age', header: 'Age', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'lastAppraisalDate',
        header: 'Last appraisal',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'appraisalAvg',
        header: 'Appraisal avg',
        sortable: true,
        render: (v) => (
          <ReportTextCell
            value={
              v != null && typeof v === 'number'
                ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : null
            }
          />
        ),
      },
      {
        key: 'overallEvaluation',
        header: 'Evaluation',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      {
        key: 'toBePromoted',
        header: 'Promote',
        sortable: true,
        render: (v) =>
          v ? (
            <Badge variant="success" className="rounded-md px-2 py-0.5">
              Yes
            </Badge>
          ) : (
            <ReportTextCell value="" />
          ),
      },
      {
        key: 'appraisalVessel',
        header: 'Appraisal vessel',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'lastVessel', header: 'Last vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'lastRank', header: 'Last rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'lastSignOffDate',
        header: 'Last S/Off',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availableFrom',
        header: 'Av. from',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availableTo',
        header: 'Av. to',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availabilityStatus',
        header: 'Av. status',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
    ],
    [],
  );

  if (!focus || !meta) {
    return <Navigate to="/crew/reports/operations/onboard" replace />;
  }

  return (
    <StatsPageShell
      hintTitle={meta.title}
      hintBody={meta.body}
      filterMode="vessel"
      trailingActions={({ applied }) =>
        applied ? (
          <Link
            to={
              focus === 'onboard'
                ? statsOnboardPrintPath({ vesselId: applied.vesselId })
                : focus === 'available'
                  ? statsAvailablePrintPath({ vesselId: applied.vesselId })
                  : statsNotRecommendedPrintPath({ vesselId: applied.vesselId })
            }
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        ) : null
      }
    >
      {({ data }) => {
        const lists = data.lists;
        const onboard = lists?.onboard ?? [];
        const available = lists?.available ?? [];
        const notRecommended = lists?.notRecommended ?? [];

        return (
          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <CrewDataTableFrame>
              {focus === 'onboard' ? (
                <DataTable
                  data={onboard}
                  columns={onboardColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'available' ? (
                <DataTable
                  data={available}
                  columns={availableColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'not-recommended' ? (
                <DataTable
                  data={notRecommended}
                  columns={nrColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
            </CrewDataTableFrame>
          </div>
        );
      }}
    </StatsPageShell>
  );
};

export const ComplianceStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const focus = isComplianceFocus(focusParam) ? focusParam : null;
  const meta = focus ? COMPLIANCE_META[focus] : null;

  const certColumns: Column<CrewStatsCertRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'crewRef', header: 'Ref', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certificate', header: 'Certificate', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certificateCode', header: 'Code', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'category', header: 'Category', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certNumber', header: 'Number', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'issueCountry',
        header: 'Issue country',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      {
        key: 'issuedDate',
        header: 'Issued',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'expiredDate',
        header: 'Expiry',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'daysUntilExpiry',
        header: 'Days',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      {
        key: 'onVessel',
        header: 'On vessel',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? 'Yes' : 'No'} />,
      },
      { key: 'certFileName', header: 'File', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'notes', header: 'Notes', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'bucket', header: 'Bucket', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const medicalColumns: Column<CrewStatsMedicalRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'crewRef', header: 'Ref', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'kind', header: 'Kind', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'examDate',
        header: 'Exam date',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'nextDueDate',
        header: 'Next due',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'daysUntilExpiry',
        header: 'Days',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'result', header: 'Result', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'resultNotes',
        header: 'Result notes',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'clinicName', header: 'Clinic', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'certificateNo',
        header: 'Certificate no.',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'notes', header: 'Notes', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const coeColumns: Column<CrewStatsCoeRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'coeDate',
        header: 'COE',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      { key: 'daysToCoe', header: 'Days to COE', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'intentionSignOffDate',
        header: 'Intention S/O',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      { key: 'monthsOnboard', header: 'Months', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const mixColumns: Column<CrewStatMixItem>[] = useMemo(
    () => [
      { key: 'label', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'count', header: 'Onboard', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  if (!focus || !meta) {
    return <Navigate to="/crew/reports/compliance/certs-expired" replace />;
  }

  return (
    <StatsPageShell
      hintTitle={meta.title}
      hintBody={meta.body}
      filterMode="vessel-days"
      trailingActions={({ applied }) =>
        applied ? (
          <Link
            to={statsCompliancePrintPath(focus, {
              vesselId: applied.vesselId,
              withinDays: applied.withinDays,
            })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        ) : null
      }
    >
      {({ data }) => {
        const k = data.kpis;
        const lists = data.lists;
        const certsExpired = lists?.certsExpired ?? [];
        const certsExpiring = lists?.certsExpiring ?? [];
        const medicalsExpired = lists?.medicalsExpired ?? [];
        const medicalsExpiring = lists?.medicalsExpiring ?? [];
        const coeSoon = lists?.coeSoon ?? [];
        const intentionOverdue = lists?.intentionOverdue ?? [];
        const nationalities = data.charts.nationalityMix;

        const countValue = (() => {
          switch (focus) {
            case 'certs-expired':
              return formatStatNum(k.certsExpired);
            case 'certs-expiring':
              return formatStatNum(k.certsExpiring);
            case 'medicals-expired':
              return formatStatNum(k.medicalsExpired);
            case 'medicals-due':
              return formatStatNum(k.medicalsExpiring);
            case 'coe-soon':
              return formatStatNum(k.coeSoon);
            case 'intention-overdue':
              return formatStatNum(k.intentionOverdue);
            case 'nationalities':
              return formatStatNum(nationalities.length);
          }
        })();

        const toneCount =
          focus === 'certs-expired'
            ? k.certsExpired
            : focus === 'certs-expiring'
              ? k.certsExpiring
              : focus === 'medicals-expired'
                ? k.medicalsExpired
                : focus === 'medicals-due'
                  ? k.medicalsExpiring
                  : focus === 'coe-soon'
                    ? k.coeSoon
                    : focus === 'intention-overdue'
                      ? k.intentionOverdue
                      : null;

        const emptyMessage = meta.empty;

        return (
          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <div className="shrink-0 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <KpiTile
                label={meta.kpiLabel}
                value={countValue}
                hint={
                  focus === 'nationalities'
                    ? 'Distinct nationalities onboard'
                    : undefined
                }
                tone={
                  meta.tone && toneCount != null
                    ? kpiTone(toneCount)
                    : 'default'
                }
              />
            </div>
            <CrewDataTableFrame>
              {focus === 'certs-expired' ? (
                <DataTable
                  data={certsExpired}
                  columns={certColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={emptyMessage}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'certs-expiring' ? (
                <DataTable
                  data={certsExpiring}
                  columns={certColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={emptyMessage}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'medicals-expired' ? (
                <DataTable
                  data={medicalsExpired}
                  columns={medicalColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={emptyMessage}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'medicals-due' ? (
                <DataTable
                  data={medicalsExpiring}
                  columns={medicalColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={emptyMessage}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'coe-soon' ? (
                <DataTable
                  data={coeSoon}
                  columns={coeColumns}
                  keyExtractor={(row) => row.transactionId}
                  emptyMessage={emptyMessage}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'intention-overdue' ? (
                <DataTable
                  data={intentionOverdue}
                  columns={coeColumns}
                  keyExtractor={(row) => row.transactionId}
                  emptyMessage={emptyMessage}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'nationalities' ? (
                <DataTable
                  data={nationalities}
                  columns={mixColumns}
                  keyExtractor={(row) => row.label}
                  emptyMessage={emptyMessage}
                />
              ) : null}
            </CrewDataTableFrame>
          </div>
        );
      }}
    </StatsPageShell>
  );
};

type PeopleFocus =
  | 'appraisals'
  | 'not-recommended'
  | 'avg-months-onboard'
  | 'contract-wages'
  | 'allottees'
  | 'allottee-amount';

const PEOPLE_META: Record<PeopleFocus, { title: string; body: string; kpiLabel: string; empty: string }> = {
  appraisals: {
    title: 'Appraisals / NR set',
    body: 'Crew with appraisals or NR flag set.',
    kpiLabel: 'Appraisals / NR set',
    empty: 'No appraisal / NR records'},
  'not-recommended': {
    title: 'NR count',
    body: 'Crew flagged as not recommended, with last appraisal, evaluation average, and last tour context.',
    kpiLabel: 'NR count',
    empty: 'No not-recommended crew'},
  'avg-months-onboard': {
    title: 'Avg months onboard',
    body: 'Average months on the current ONB tour.',
    kpiLabel: 'Avg months onboard',
    empty: 'No onboard crew'},
  'contract-wages': {
    title: 'Contract wage total',
    body: 'Sum of contract wages for period assignments.',
    kpiLabel: 'Contract wage total',
    empty: 'No crew in period'},
  allottees: {
    title: 'Allottees',
    body: 'Count of standing allottees.',
    kpiLabel: 'Allottees',
    empty: 'No crew in period'},
  'allottee-amount': {
    title: 'Allottee amount',
    body: 'Total standing allottee amounts.',
    kpiLabel: 'Allottee amount',
    empty: 'No crew in period'}};

const isPeopleFocus = (v: string | undefined): v is PeopleFocus =>
  v != null && Object.prototype.hasOwnProperty.call(PEOPLE_META, v);

export const PeoplePayrollStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const focus = isPeopleFocus(focusParam) ? focusParam : null;
  const meta = focus ? PEOPLE_META[focus] : null;

  const appraisalColumns: Column<CrewStatsAppraisalRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'notRecommended',
        header: 'NR',
        sortable: true,
        render: (v) =>
          v ? (
            <Badge variant="danger" className="rounded-md px-2 py-0.5">
              NR
            </Badge>
          ) : (
            <ReportTextCell value="" />
          )},
      {
        key: 'lastAppraisalDate',
        header: 'Last appraisal',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      {
        key: 'overallEvaluation',
        header: 'Evaluation',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />},
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const nrColumns: Column<CrewStatsNrRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      {
        key: 'crewRef',
        header: 'Ref',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'nationality', header: 'Nationality', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'age', header: 'Age', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'lastAppraisalDate',
        header: 'Last appraisal',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'appraisalAvg',
        header: 'Appraisal avg',
        sortable: true,
        render: (v) => (
          <ReportTextCell
            value={
              v != null && typeof v === 'number'
                ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                : null
            }
          />
        ),
      },
      {
        key: 'overallEvaluation',
        header: 'Evaluation',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      {
        key: 'toBePromoted',
        header: 'Promote',
        sortable: true,
        render: (v) =>
          v ? (
            <Badge variant="success" className="rounded-md px-2 py-0.5">
              Yes
            </Badge>
          ) : (
            <ReportTextCell value="" />
          ),
      },
      {
        key: 'appraisalVessel',
        header: 'Appraisal vessel',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      { key: 'lastVessel', header: 'Last vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'lastRank', header: 'Last rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'lastSignOffDate',
        header: 'Last S/Off',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availableFrom',
        header: 'Av. from',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availableTo',
        header: 'Av. to',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'availabilityStatus',
        header: 'Av. status',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
    ],
    [],
  );

  const onboardColumns: Column<CrewStatsOnboardRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'signOnDate',
        header: 'Sign on',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />},
      { key: 'monthsOnboard', header: 'Months', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const wageColumns: Column<CrewStatsWageRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'vessel', header: 'Vessel', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'status', header: 'Status', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'wage',
        header: 'Wage',
        sortable: true,
        render: (_v, row) => (
          <ReportTextCell
            value={
              row.wage == null
                ? null
                : `${row.wage.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${
                    row.wageCurrency ? ` ${row.wageCurrency}` : ''
                  }`
            }
          />
        )},
      { key: 'allotteeCount', header: 'Allottees', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'allotteeAmount',
        header: 'Allottee total',
        sortable: true,
        render: (v) => (
          <ReportTextCell
            value={
              typeof v === 'number'
                ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : v
            }
          />
        )},
    ],
    [],
  );

  if (!focus || !meta) {
    return <Navigate to="/crew/statistics/people-payroll/appraisals" replace />;
  }

  return (
    <StatsPageShell hintTitle={meta.title} hintBody={meta.body} filterMode="vessel-period">
      {({ data }) => {
        const k = data.kpis;
        const lists = data.lists;
        const appraisals = lists?.appraisals ?? [];
        const notRecommended = lists?.notRecommended ?? [];
        const onboard = lists?.onboard ?? [];
        const wages = lists?.wages ?? [];

        const countValue = (() => {
          switch (focus) {
            case 'appraisals':
              return formatStatNum(k.appraisalCrewCount);
            case 'not-recommended':
              return formatStatNum(k.nrCount);
            case 'avg-months-onboard':
              return k.avgMonthsOnboard == null ? '—' : formatStatNum(k.avgMonthsOnboard, 1);
            case 'contract-wages':
              return formatStatNum(k.totalWage, 2);
            case 'allottees':
              return formatStatNum(k.allotteeCount);
            case 'allottee-amount':
              return formatStatNum(k.allotteeAmount, 2);
          }
        })();

        return (
          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <div className="shrink-0 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <KpiTile
                label={meta.kpiLabel}
                value={countValue}
                hint={
                  focus === 'appraisals'
                    ? 'Crew with appraisals or NR flag'
                    : focus === 'avg-months-onboard'
                      ? 'Current ONB tour'
                      : focus === 'contract-wages'
                        ? 'Period assignments'
                        : focus === 'allottee-amount'
                          ? 'Standing allottee totals'
                          : undefined
                }
                tone={focus === 'not-recommended' ? kpiTone(k.nrCount) : 'default'}
              />
            </div>
            <CrewDataTableFrame>
              {focus === 'appraisals' ? (
                <DataTable
                  data={appraisals}
                  columns={appraisalColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'not-recommended' ? (
                <DataTable
                  data={notRecommended}
                  columns={nrColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'avg-months-onboard' ? (
                <DataTable
                  data={onboard}
                  columns={onboardColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
              {focus === 'contract-wages' || focus === 'allottees' || focus === 'allottee-amount' ? (
                <DataTable
                  data={wages}
                  columns={wageColumns}
                  keyExtractor={(row) => row.crewId}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
            </CrewDataTableFrame>
          </div>
        );
      }}
    </StatsPageShell>
  );
};

type MixFocus = 'nationality' | 'rank' | 'cert-expiry';

const MIX_META: Record<MixFocus, { title: string; body: string; empty: string }> = {
  nationality: {
    title: 'Nationality mix',
    body: 'Onboard headcount by nationality.',
    empty: 'No onboard crew'},
  rank: {
    title: 'Rank mix',
    body: 'Onboard headcount by rank.',
    empty: 'No onboard crew'},
  'cert-expiry': {
    title: 'Certificate expiry buckets',
    body: 'Certificate expiry timing buckets with detail list.',
    empty: 'No certificates in window'}};

const isMixFocus = (v: string | undefined): v is MixFocus =>
  v != null && Object.prototype.hasOwnProperty.call(MIX_META, v);

export const MixChartsStatisticsPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const { focus: focusParam } = useParams<{ focus?: string }>();
  const focus = isMixFocus(focusParam) ? focusParam : null;
  const meta = focus ? MIX_META[focus] : null;

  const mixColumns: Column<CrewStatMixItem>[] = useMemo(
    () => [
      { key: 'label', header: 'Label', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'count', header: 'Count', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  const certBucketColumns: Column<CrewStatsCertRow>[] = useMemo(
    () => [
      { key: 'name', header: 'Crew', sortable: true, render: (_v, row) => crewLink(row.crewId, row.name) },
      { key: 'crewRef', header: 'Ref', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certificate', header: 'Certificate', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certificateCode', header: 'Code', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'category', header: 'Category', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'certNumber', header: 'Number', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      {
        key: 'issueCountry',
        header: 'Issue country',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      {
        key: 'issuedDate',
        header: 'Issued',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'expiredDate',
        header: 'Expiry',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? formatDate(v) : null} />,
      },
      {
        key: 'daysUntilExpiry',
        header: 'Days',
        sortable: true,
        render: (v) => <ReportTextCell value={v} />,
      },
      {
        key: 'onVessel',
        header: 'On vessel',
        sortable: true,
        render: (v) => <ReportTextCell value={v ? 'Yes' : 'No'} />,
      },
      { key: 'certFileName', header: 'File', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'notes', header: 'Notes', sortable: true, render: (v) => <ReportTextCell value={v} /> },
      { key: 'bucket', header: 'Bucket', sortable: true, render: (v) => <ReportTextCell value={v} /> },
    ],
    [],
  );

  if (!focus || !meta) {
    return <Navigate to="/crew/statistics/mix/nationality" replace />;
  }

  return (
    <StatsPageShell hintTitle={meta.title} hintBody={meta.body} filterMode="vessel-days">
      {({ data }) => {
        const nationalityMix = data.charts.nationalityMix;
        const rankMix = data.charts.rankMix;
        const buckets = data.charts.certExpiryBuckets;
        const certBuckets = data.lists?.certBuckets ?? [];

        return (
          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <div className="shrink-0">
              {focus === 'nationality' ? (
                <HorizontalBars title="Nationality mix" data={nationalityMix} emptyLabel="No onboard crew" />
              ) : null}
              {focus === 'rank' ? (
                <HorizontalBars title="Rank mix" data={rankMix} emptyLabel="No onboard crew" />
              ) : null}
              {focus === 'cert-expiry' ? <BucketBars title="Certificate expiry buckets" data={buckets} /> : null}
            </div>
            <CrewDataTableFrame>
              {focus === 'nationality' ? (
                <DataTable
                  data={nationalityMix}
                  columns={mixColumns}
                  keyExtractor={(row) => row.label}
                  emptyMessage={meta.empty}
                />
              ) : null}
              {focus === 'rank' ? (
                <DataTable
                  data={rankMix}
                  columns={mixColumns}
                  keyExtractor={(row) => row.label}
                  emptyMessage={meta.empty}
                />
              ) : null}
              {focus === 'cert-expiry' ? (
                <DataTable
                  data={certBuckets}
                  columns={certBucketColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage={meta.empty}
                  onRowClick={(row) => openCrew(row.crewId)}
                  actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
                  actionsPosition="left"
                />
              ) : null}
            </CrewDataTableFrame>
          </div>
        );
      }}
    </StatsPageShell>
  );
};
